// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

// Every grade of the card on screen in one table: the editable enhanced grades, the
// RoboGrades, and underneath the AI reference grades, by imaging source, one row per model.

import React, { useState } from 'react';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import {
    BarcodeOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    LoadingOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { min } from 'lodash';

import CVATTooltip from 'components/common/cvat-tooltip';
import {
    AiGradeProvider, AiGradeResult, AiGradeSource, AiGradesState, AiModelsState, AiSpend, GradesState,
} from 'reducers/interfaces';
import { computeTotal, getGradeNickname } from 'utils/grades';
import {
    AI_GRADE_PROVIDER_LABELS, AI_GRADE_PROVIDERS, AI_GRADE_SOURCE_LABELS, AI_GRADE_SOURCES,
    formatCost, formatTokens, isAiGradeBusy, shortModelName, sortAiGradeResults,
} from 'utils/ai-grades';
import AiFindingsModal from './ai-findings-modal';
import AiSparklesIcon from './ai-sparkles-icon';

type GradeValues = GradesState['values'];
type Side = 'front' | 'back';
const SIDES: Side[] = ['front', 'back'];
const SUBGRADES = [
    ['centering', 'Centering'],
    ['corners', 'Corners'],
    ['edges', 'Edges'],
    ['surface', 'Surface'],
] as const;
type Subgrade = typeof SUBGRADES[number][0];
// the source, four subgrades and a total per side, the overall and the cost
const COLUMN_COUNT = 1 + SIDES.length * (SUBGRADES.length + 1) + 2;

export interface RowVisibility {
    enhanced: boolean;
    robogrades: boolean;
    predicted: boolean;
    ai: boolean;
}

// overall = 0.6 front + 0.4 back
export const overallOf = (front: number | string | undefined, back: number | string | undefined): number => (
    Math.round((0.6 * Number(front || 0) + 0.4 * Number(back || 0)) * 100) / 100
);

const roundBy25 = (value: string | number | undefined): string => (
    (Math.round(Number(value || 0) * 4) / 4).toFixed(2)
);
const roundBy25Centering = (value: string | number | undefined): string => (
    Number(value || 0) >= 9.6 ? '10.00' : roundBy25(value)
);

// the grade values come as "8.50" strings from the AGS API (mapGradeValue), as numbers once edited
const gradeOf = (values: Partial<GradeValues>, key: string): number | string | undefined => (
    (values as Record<string, number | string | undefined>)[key]
);

function enhancedTotal(values: Partial<GradeValues>, side: Side): number {
    return Number(computeTotal(SUBGRADES.map(([part]) => Number(gradeOf(values, `${side}_${part}_human_grade`) ?? 0))));
}

// the lowest of the four overall RoboGrade subgrades plus one caps the reported overall RoboGrade
function robogradeCap(values: Partial<GradeValues>): number {
    const lowest = min(SUBGRADES.map(([part]) => overallOf(
        gradeOf(values, `front_${part}_laser_grade`),
        gradeOf(values, `back_${part}_laser_grade`),
    ))) ?? 0;
    return Math.min(10, lowest + 1);
}

const CAPPED = 'Capped at the lowest combined subgrade plus one';

function Value({ value }: { value: number | string | null | undefined }): JSX.Element {
    const number = Number(value);
    if (value === null || value === undefined || Number.isNaN(number) || number === 0) {
        return <span className='grades-table-empty'>—</span>;
    }
    return <span>{number.toFixed(2)}</span>;
}

function Overall({ value, crossedOut }: { value: number | null; crossedOut?: number }): JSX.Element {
    if (!value) {
        return <span className='grades-table-empty'>—</span>;
    }
    return (
        <span className='grades-table-overall'>
            {crossedOut !== undefined ? <del>{crossedOut.toFixed(2)}</del> : null}
            <strong>{value.toFixed(2)}</strong>
            <span className='grades-table-nickname'>{getGradeNickname(value)}</span>
        </span>
    );
}

// an overall the +1 rule lowered shows the overall before the cap, crossed out
function CappedOverall({ value, uncapped }: { value: number | null; uncapped: number | null }): JSX.Element {
    if (value && uncapped && value < uncapped) {
        return (
            <CVATTooltip title={CAPPED}>
                <span>
                    <Overall value={value} crossedOut={uncapped} />
                </span>
            </CVATTooltip>
        );
    }
    return <Overall value={value} />;
}

const Skeleton = (): JSX.Element => <span className='grades-table-skeleton' />;

function SideCells({ side, cell, total }: {
    side: Side;
    cell: (part: Subgrade) => React.ReactNode;
    total: React.ReactNode;
}): JSX.Element {
    return (
        <>
            {SUBGRADES.map(([part], index) => (
                <td key={`${side}-${part}`} className={index === 0 ? 'grades-table-side-start' : undefined}>
                    {cell(part)}
                </td>
            ))}
            <td className='grades-table-total'>{total}</td>
        </>
    );
}

function RowTitle({ title, detail, extra }: {
    title: React.ReactNode;
    detail?: React.ReactNode;
    extra?: React.ReactNode;
}): JSX.Element {
    return (
        <th scope='row' className='grades-table-source'>
            <div className='grades-table-source-content'>
                <div className='grades-table-source-text'>
                    <span className='grades-table-source-title'>{title}</span>
                    {detail ? <span className='grades-table-source-detail'>{detail}</span> : null}
                </div>
                {extra}
            </div>
        </th>
    );
}

function AiRow({
    result, retryDisabled, onRetry, onOpenFindings,
}: {
    result: AiGradeResult;
    retryDisabled: boolean;
    onRetry(result: AiGradeResult): void;
    onOpenFindings(result: AiGradeResult): void;
}): JSX.Element {
    const busy = isAiGradeBusy(result);
    const failed = result.status === 'error';
    const { grades } = result;

    const findings = grades && !busy ? (
        <Button
            size='small'
            icon={<AiSparklesIcon />}
            onClick={(event) => {
                event.stopPropagation();
                onOpenFindings(result);
            }}
            className='grades-table-findings-button'
        >
            Findings
        </Button>
    ) : null;
    let extra: React.ReactNode = findings;
    if (busy) {
        extra = <LoadingOutlined className='grades-table-status' />;
    } else if (failed) {
        const error = grades ?
            `The last run failed; these grades are from the one before. ${result.error}` :
            result.error;
        extra = (
            <span className='grades-table-status'>
                <CVATTooltip title={error}>
                    <ExclamationCircleFilled className='grades-table-status-error' />
                </CVATTooltip>
                <CVATTooltip title='Try this row again'>
                    <Button
                        type='text'
                        size='small'
                        icon={<ReloadOutlined />}
                        aria-label='Try this row again'
                        disabled={retryDisabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            onRetry(result);
                        }}
                    />
                </CVATTooltip>
                {findings}
            </span>
        );
    }

    // one line per model: its id and effort show on hover, and in the findings
    const modelTitle = `${result.model}${result.effort ? ` · ${result.effort} effort` : ''}`;
    const tokens = result.usage ?
        `${formatTokens(result.usage.inputTokens)} input and ${formatTokens(result.usage.outputTokens)} output tokens` :
        '';
    let cost: React.ReactNode = <span className='grades-table-empty'>—</span>;
    if (result.costUsd !== null) {
        cost = (
            <CVATTooltip title={tokens}>
                <span>{formatCost(result.costUsd)}</span>
            </CVATTooltip>
        );
    } else if (result.usage) {
        cost = (
            <CVATTooltip title={`No price for ${result.model} on the server; ${tokens}`}>
                <span className='grades-table-empty'>?</span>
            </CVATTooltip>
        );
    }

    const placeholder = busy ? <Skeleton /> : <span className='grades-table-empty'>—</span>;
    const className = [
        'grades-table-ai-row',
        busy && grades ? 'grades-table-row-stale' : '',
        failed && !grades ? 'grades-table-row-failed' : '',
        grades ? 'grades-table-row-clickable' : '',
    ].filter(Boolean).join(' ');
    return (
        <tr
            className={className}
            onClick={grades ? () => onOpenFindings(result) : undefined}
            onKeyDown={grades ? (event) => {
                if (event.key === 'Enter') onOpenFindings(result);
            } : undefined}
            tabIndex={grades ? 0 : undefined}
        >
            <RowTitle
                title={(
                    <>
                        <span className={`grades-table-provider-dot grades-table-provider-${result.provider}`} />
                        <CVATTooltip title={modelTitle}>
                            <span>{shortModelName(result.model)}</span>
                        </CVATTooltip>
                        <span className='grades-table-provider'>{AI_GRADE_PROVIDER_LABELS[result.provider]}</span>
                    </>
                )}
                extra={extra}
            />
            {SIDES.map((side) => (
                <SideCells
                    key={side}
                    side={side}
                    cell={(part) => (grades ? <Value value={grades[side][part]} /> : placeholder)}
                    total={grades ? <Value value={side === 'front' ? result.frontTotal : result.backTotal} /> : placeholder}
                />
            ))}
            <td className='grades-table-overall-cell'>
                {grades ? <CappedOverall value={result.overall} uncapped={result.overallUncapped} /> : placeholder}
            </td>
            <td className='grades-table-cost'>{cost}</td>
        </tr>
    );
}

// One provider's model and effort for this person's runs by hand: the defaults until they pick
// others from the provider's own list. The pick stays in their browser and goes with each run,
// so one admin's pick never changes what another grades with.
function AiModelSelect({
    provider, state, busy, onSelect, onSelectEffort,
}: {
    provider: AiGradeProvider;
    state: AiModelsState;
    busy: boolean;
    onSelect(provider: AiGradeProvider, model: string): void;
    onSelectEffort(provider: AiGradeProvider, effort: string): void;
}): JSX.Element {
    const name = AI_GRADE_PROVIDER_LABELS[provider];
    const entry = state.providers?.[provider] ?? null;
    const pick = state.picks[provider] ?? (entry ? { model: entry.defaultModel, effort: entry.defaultEffort } : null);
    const options = (entry?.models ?? []).map((model) => ({ value: model.id, label: model.name }));
    if (pick && !options.some((option) => option.value === pick.model)) {
        options.unshift({ value: pick.model, label: `${pick.model} (not listed)` });
    }
    const efforts = entry?.models.find((model) => model.id === pick?.model)?.efforts ?? [];

    let tooltip = `The model of your next ${name} grades; only yours`;
    if (!entry) {
        tooltip = state.loading ? `Loading the ${name} models` : `The ${name} models could not be loaded`;
    } else if (entry.error) {
        tooltip = entry.error;
    } else if (!state.canGenerate) {
        tooltip = `Administrators and reviewers run ${name} models`;
    }
    const locked = !entry || !state.canGenerate || !!entry.error || busy;
    const effortTooltip = efforts.length ?
        `How long ${name} reasons before it grades: higher is slower and costs more` :
        'This model has no effort setting';

    return (
        <>
            <CVATTooltip title={tooltip}>
                {/* a disabled select sends no mouse events: the span keeps the tooltip working */}
                <span className='grades-table-ai-model-wrapper'>
                    <Select
                        className={`grades-table-ai-model${entry?.error ? ' grades-table-ai-model-error' : ''}`}
                        value={pick?.model || undefined}
                        options={options}
                        loading={state.loading}
                        disabled={locked}
                        showSearch
                        optionFilterProp='label'
                        dropdownMatchSelectWidth={false}
                        onChange={(model: string) => onSelect(provider, model)}
                        aria-label={`${name} model`}
                    />
                </span>
            </CVATTooltip>
            <CVATTooltip title={effortTooltip}>
                <span className='grades-table-ai-model-wrapper'>
                    <Select
                        className='grades-table-ai-effort'
                        value={pick?.effort ?? undefined}
                        placeholder='n/a'
                        options={efforts.map((effort) => ({ value: effort, label: effort }))}
                        disabled={locked || !efforts.length}
                        dropdownMatchSelectWidth={false}
                        onChange={(effort: string) => onSelectEffort(provider, effort)}
                        aria-label={`${name} effort`}
                    />
                </span>
            </CVATTooltip>
        </>
    );
}

// One provider's button: grades the card with the picked model on every source it has, replacing
// that model's rows (or adding them, for a model the card has none of). While any of the
// provider's rows is being graded, the first run of the card included, it waits, counting them.
function AiGenerateButton({
    provider, model, rows, busy, disabledReason, onGenerate,
}: {
    provider: AiGradeProvider;
    model: string | null;
    // the provider's rows of the card
    rows: AiGradeResult[];
    // a run of the provider is starting or still grading
    busy: boolean;
    disabledReason: string | null;
    onGenerate(provider: AiGradeProvider): void;
}): JSX.Element {
    const name = AI_GRADE_PROVIDER_LABELS[provider];
    const grading = rows.filter(isAiGradeBusy).length;
    const hasRows = rows.some((row) => row.model === model);
    let label = hasRows ? `Regenerate with ${name}` : `Generate with ${name}`;
    let tooltip = disabledReason;
    if (busy) {
        label = grading ? `${name}… ${rows.length - grading}/${rows.length}` : `${name}…`;
        tooltip = tooltip ?? `${name} is grading this card`;
    } else if (!tooltip && model) {
        tooltip = `Grade this card with ${model} on every imaging source it has` +
            `${hasRows ? ', replacing its grades' : ''}`;
    }

    return (
        <CVATTooltip title={tooltip}>
            <Button
                type='primary'
                icon={<AiSparklesIcon />}
                loading={busy}
                disabled={busy || !!disabledReason || !model}
                onClick={() => onGenerate(provider)}
                className={`grades-table-ai-button grades-table-ai-button-${provider}`}
            >
                {label}
            </Button>
        </CVATTooltip>
    );
}

// What the AI grades cost, set apart from the section's description: a column for this card and,
// for administrators, one for all cards, each with its caption over its figures
function AiSpendSummary({ ai, spend }: { ai: AiGradesState; spend: AiSpend | null }): JSX.Element | null {
    if (!ai.calls && !spend) {
        return null;
    }

    let allCards: React.ReactNode = null;
    if (spend) {
        const { today, week, month } = spend;
        const unpriced = month.unpricedCalls ? `; ${month.unpricedCalls} calls without a price are not counted` : '';
        const periods: [string, number][] = [
            ['Today', today.costUsd], ['7 days', week.costUsd], ['30 days', month.costUsd],
        ];
        const cards = `${today.cards} cards today, ${week.cards} in 7 days, ${month.cards} in 30 days`;
        allCards = (
            <CVATTooltip title={`${cards}${unpriced}`}>
                <div className='grades-table-spend-item'>
                    <span className='grades-table-spend-label'>All cards</span>
                    <span className='grades-table-spend-periods'>
                        {periods.map(([label, cost]) => (
                            <span key={label}>
                                <span className='grades-table-spend-period'>{label}</span>
                                <span className='grades-table-spend-value'>{formatCost(cost)}</span>
                            </span>
                        ))}
                    </span>
                </div>
            </CVATTooltip>
        );
    }

    const unpriced = ai.unpricedCalls ? `; ${ai.unpricedCalls} without a price are not counted` : '';
    return (
        <div className='grades-table-spend'>
            {ai.calls ? (
                <CVATTooltip title={`${ai.calls} calls, replaced answers included${unpriced}`}>
                    <div className='grades-table-spend-item'>
                        <span className='grades-table-spend-label'>This card</span>
                        <span className='grades-table-spend-value'>{formatCost(ai.spentUsd)}</span>
                    </div>
                </CVATTooltip>
            ) : null}
            {allCards}
        </div>
    );
}

interface Props {
    values: GradeValues;
    liveValues: Partial<GradeValues>;
    visibility: RowVisibility;
    // the AI grades of the card on screen
    ai: AiGradesState;
    // the imaging sources this card has on both sides
    aiSources: AiGradeSource[];
    aiDisabledReason: string | null;
    aiModels: AiModelsState;
    aiSpend: AiSpend | null;
    onGenerateAi(provider: AiGradeProvider): void;
    onRetryAi(result: AiGradeResult): void;
    onSelectAiModel(provider: AiGradeProvider, model: string): void;
    onSelectAiEffort(provider: AiGradeProvider, effort: string): void;
}

function GradesTable(props: Props): JSX.Element {
    const {
        values, liveValues, visibility, ai, aiSources, aiDisabledReason, aiModels, aiSpend,
        onGenerateAi, onRetryAi, onSelectAiModel, onSelectAiEffort,
    } = props;

    const enhancedFront = enhancedTotal(liveValues, 'front');
    const enhancedBack = enhancedTotal(liveValues, 'back');
    const roboOverall = overallOf(values.front_overall_from_subgrades, values.back_overall_from_subgrades);
    const cap = robogradeCap(values);
    const roboCapped = roboOverall && cap < roboOverall;

    const [findings, setFindings] = useState<{ source: AiGradeSource; model: string } | null>(null);
    const openedResult = findings ? ai.results.find((result) => (
        result.source === findings.source && result.model === findings.model
    )) ?? null : null;

    const results = sortAiGradeResults(ai.results, aiModels);
    const sources = AI_GRADE_SOURCES.filter((source) => results.some((result) => result.source === source));
    const missingSources = AI_GRADE_SOURCES
        .filter((source) => !aiSources.includes(source))
        .map((source) => AI_GRADE_SOURCE_LABELS[source].file);
    // a provider is busy while its run is starting or any of its rows is being graded
    const providerBusy = (provider: AiGradeProvider): boolean => (
        ai.starting.includes(provider) ||
        ai.results.some((result) => result.provider === provider && isAiGradeBusy(result))
    );

    let aiPlaceholder: string | null = null;
    if (!ai.loaded) {
        aiPlaceholder = 'Loading the AI grades of this card…';
    } else if (!results.length && !ai.error) {
        aiPlaceholder = ai.canGenerate && !aiDisabledReason ?
            'Starting the first run of this card…' :
            'No AI grades for this card yet: they are made the first time an administrator or a reviewer opens it.';
    }

    const showRobo = visibility.enhanced || visibility.robogrades || visibility.predicted;
    return (
        <div className='grades-table-wrapper'>
            <table className='grades-table'>
                <thead>
                    <tr>
                        <th rowSpan={2} scope='col' className='grades-table-source'>Source</th>
                        {SIDES.map((side) => (
                            <th key={side} colSpan={SUBGRADES.length + 1} scope='colgroup' className='grades-table-side'>
                                {side === 'front' ? 'Front' : 'Back'}
                            </th>
                        ))}
                        <th rowSpan={2} scope='col' className='grades-table-overall-cell'>Overall</th>
                        <th rowSpan={2} scope='col' className='grades-table-cost'>Cost</th>
                    </tr>
                    <tr>
                        {SIDES.map((side) => (
                            <React.Fragment key={side}>
                                {SUBGRADES.map(([part, title], index) => (
                                    <th
                                        key={part}
                                        scope='col'
                                        className={index === 0 ? 'grades-table-side-start' : undefined}
                                    >
                                        {title}
                                    </th>
                                ))}
                                <th scope='col' className='grades-table-total'>Total</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>

                {showRobo ? (
                    <tbody>
                        <tr className='grades-table-section'>
                            <td colSpan={COLUMN_COUNT}>
                                <div className='grades-table-section-content'>
                                    <span className='grades-table-section-title'>
                                        <BarcodeOutlined />
                                        RoboGrades
                                    </span>
                                    <span className='grades-table-section-hint'>
                                        Enhanced grades are editable and saved with Update Enhanced RoboGrades.
                                    </span>
                                </div>
                            </td>
                        </tr>
                        {visibility.enhanced ? (
                            <tr className='grades-table-enhanced-row'>
                                <RowTitle
                                    title='Enhanced RoboGrades'
                                    detail='Reviewed by a grader'
                                    extra={<EditOutlined className='grades-table-status' />}
                                />
                                {SIDES.map((side) => (
                                    <SideCells
                                        key={side}
                                        side={side}
                                        cell={(part) => (
                                            <Form.Item name={`${side}_${part}_human_grade`} noStyle>
                                                <InputNumber
                                                    size='small'
                                                    min={0}
                                                    max={10}
                                                    step={0.25}
                                                    formatter={part === 'centering' ? roundBy25Centering : roundBy25}
                                                    className='grades-table-input'
                                                    aria-label={`${side} ${part} enhanced grade`}
                                                />
                                            </Form.Item>
                                        )}
                                        total={<Value value={side === 'front' ? enhancedFront : enhancedBack} />}
                                    />
                                ))}
                                <td className='grades-table-overall-cell'>
                                    <Overall value={overallOf(enhancedFront, enhancedBack)} />
                                </td>
                                <td className='grades-table-cost' />
                            </tr>
                        ) : null}
                        {visibility.robogrades ? (
                            <tr>
                                <RowTitle title='RoboGrades' detail='Laser scan model' />
                                {SIDES.map((side) => (
                                    <SideCells
                                        key={side}
                                        side={side}
                                        cell={(part) => (
                                            <Value value={gradeOf(values, `${side}_${part}_laser_grade`)} />
                                        )}
                                        total={<Value value={gradeOf(values, `${side}_overall_from_subgrades`)} />}
                                    />
                                ))}
                                <td className='grades-table-overall-cell'>
                                    {roboCapped ? (
                                        <CVATTooltip title={CAPPED}>
                                            <span>
                                                <Overall value={cap} crossedOut={roboOverall} />
                                            </span>
                                        </CVATTooltip>
                                    ) : <Overall value={roboOverall} />}
                                </td>
                                <td className='grades-table-cost' />
                            </tr>
                        ) : null}
                        {visibility.predicted ? (
                            <tr>
                                <RowTitle title='Predicted RoboGrade' detail='Overall only, no subgrades' />
                                {SIDES.map((side) => (
                                    <SideCells
                                        key={side}
                                        side={side}
                                        cell={() => <span className='grades-table-empty'>—</span>}
                                        total={<Value value={gradeOf(values, `${side}_overall_predicted_grade`)} />}
                                    />
                                ))}
                                <td className='grades-table-overall-cell'>
                                    <Overall
                                        value={overallOf(
                                            values.front_overall_predicted_grade,
                                            values.back_overall_predicted_grade,
                                        )}
                                    />
                                </td>
                                <td className='grades-table-cost' />
                            </tr>
                        ) : null}
                    </tbody>
                ) : null}

                {visibility.ai ? (
                    <tbody>
                        <tr className='grades-table-section'>
                            <td colSpan={COLUMN_COUNT}>
                                <div className='grades-table-section-content'>
                                    <span className='grades-table-section-title'>
                                        <AiSparklesIcon />
                                        AI reference grades
                                    </span>
                                    <span className='grades-table-section-hint'>
                                        OpenAI and Claude models grade this card from each imaging source, for
                                        internal comparison.
                                        {missingSources.length ? (
                                            ` This card has no ${missingSources.join(' or ')}.`
                                        ) : null}
                                    </span>
                                    <AiSpendSummary ai={ai} spend={aiModels.canSeeSpend ? aiSpend : null} />
                                    <div className='grades-table-ai-buttons'>
                                        {AI_GRADE_PROVIDERS.map((provider) => {
                                            const busy = providerBusy(provider);
                                            const model = aiModels.picks[provider]?.model ??
                                                aiModels.providers?.[provider]?.defaultModel ?? null;
                                            const rows = ai.results.filter((result) => result.provider === provider);
                                            return (
                                                <div key={provider} className='grades-table-ai-provider'>
                                                    <AiModelSelect
                                                        provider={provider}
                                                        state={aiModels}
                                                        busy={busy}
                                                        onSelect={onSelectAiModel}
                                                        onSelectEffort={onSelectAiEffort}
                                                    />
                                                    <AiGenerateButton
                                                        provider={provider}
                                                        model={model}
                                                        rows={rows}
                                                        busy={busy}
                                                        disabledReason={aiDisabledReason}
                                                        onGenerate={onGenerateAi}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {ai.error ? (
                                    <div className='grades-table-section-error'>
                                        <ExclamationCircleFilled />
                                        {ai.error}
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                        {aiPlaceholder ? (
                            <tr className='grades-table-placeholder'>
                                <td colSpan={COLUMN_COUNT}>
                                    <span className='grades-table-placeholder-text'>{aiPlaceholder}</span>
                                </td>
                            </tr>
                        ) : null}
                        {sources.map((source) => (
                            <React.Fragment key={source}>
                                <tr className='grades-table-group'>
                                    <th colSpan={COLUMN_COUNT} scope='rowgroup'>
                                        <span className='grades-table-group-title'>
                                            {AI_GRADE_SOURCE_LABELS[source].title}
                                        </span>
                                        <span className='grades-table-group-detail'>
                                            {AI_GRADE_SOURCE_LABELS[source].detail}
                                        </span>
                                    </th>
                                </tr>
                                {results.filter((result) => result.source === source).map((result) => (
                                    <AiRow
                                        key={`${result.source}:${result.model}`}
                                        result={result}
                                        retryDisabled={!!aiDisabledReason || providerBusy(result.provider)}
                                        onRetry={onRetryAi}
                                        onOpenFindings={(opened) => setFindings({
                                            source: opened.source, model: opened.model,
                                        })}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                ) : null}
            </table>
            <AiFindingsModal result={openedResult} onClose={() => setFindings(null)} />
        </div>
    );
}

export default React.memo(GradesTable);
